import type { TmsEventStream } from "@storyboard/web-client";
import { createLogger } from "@/logger";
import type { PollerClock } from "@/models/sync/eventLogSync";
import { browserPollerClock, startPolling } from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { TMS_EVENT_STREAMS } from "@/models/tmsEvent/stream";
import { walkTmsEventDelta } from "@/models/tmsEvent/tmsEventSync";

const log = createLogger("appsync");

/** How often a visible tab walks every stream's delta, ping or no ping. */
export const TMS_EVENT_POLL_INTERVAL_MS = 60_000;

/** Spread on the interval so tabs that booted together do not poll in lockstep. */
export const TMS_EVENT_POLL_JITTER_MS = 5_000;

/** The next tick's delay: the interval plus or minus the jitter. */
export function nextTmsEventPollDelayMs(random: () => number = Math.random): number {
  return TMS_EVENT_POLL_INTERVAL_MS + Math.round((random() * 2 - 1) * TMS_EVENT_POLL_JITTER_MS);
}

/** What the poller drives; production binds the delta walk, the browser's timers, and the document's visibility. */
export type TmsEventPollerDeps = PollerClock & {
  walk: (ctx: SyncContext, stream: TmsEventStream) => Promise<void>;
};

/**
 * Walks every stream's delta on a timer for as long as the workspace is open.
 *
 * A ping brings a walk forward; this is the floor under it, so an event is never more than about a minute late
 * whether or not the ping arrived. Each tick walks the streams together and waits for them, so a slow server
 * stretches the cadence rather than stacking walks; the walks themselves are single-flight, so a tick that lands on
 * a running walk coalesces into one follow-up. A hidden tab lets its ticks pass and walks once when shown again.
 *
 * @returns Stops the poller; safe to call more than once.
 */
export function startTmsEventPolling(ctx: SyncContext, deps: TmsEventPollerDeps): () => void {
  const walkAll = async () => {
    await Promise.all(
      TMS_EVENT_STREAMS.map(async (stream) => {
        try {
          await deps.walk(ctx, stream);
        } catch (error) {
          log.warn("[sync] TMS event poll failed; the next tick retries", { stream, error });
        }
      }),
    );
  };

  return startPolling(walkAll, deps);
}

/** The poller over the browser: the production delta walk, `window` timers, and `document.visibilityState`. */
export function startTmsEventPollingInBrowser(ctx: SyncContext): () => void {
  return startTmsEventPolling(ctx, { ...browserPollerClock(nextTmsEventPollDelayMs), walk: walkTmsEventDelta });
}
